import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IBullhornFields,
  ICandidateWeights,
  ICompanyWeights,
  IContactWeights,
  ICustomPairings,
  IEntityWeights
} from './weights-interfaces';

@Injectable()
export class WeightService {
  weights: IEntityWeights;
  weightsChanged: Subject<boolean>;
  customWeightSelection: ICustomPairings;
  BHFieldOptions: IBullhornFields;
  availableBHOptions: IBullhornFields;
  hasChanged = {
    candidate: false,
    contact: false,
    company: false
  };
  needsResync = {
    candidate: false,
    contact: false,
    company: false
  };

  constructor(
    private http: HttpClient
  ) {
  }

  getCandidateWeights(): Observable<ICandidateWeights> {
    return this.http.get<ICandidateWeights>(`${environment.duplikitUrl}/candidate/getWeights`);
  }

  saveCandidateWeights(weightConfig: any, redupe: boolean, resync = false): Observable<any> {
    return this.http.post<ICandidateWeights>(
      `${environment.duplikitUrl}/Candidate/saveWeights?refreshDupes=${redupe}&resync=${resync}`, weightConfig);
  }

  getContactWeights(): Observable<any> {
    return this.http.get<IContactWeights>(`${environment.duplikitUrl}/contact/getWeights`);
  }

  saveContactWeights(weightConfig: any, redupe: boolean, resync = false): Observable<any> {
    return this.http.post<IContactWeights>(
      `${environment.duplikitUrl}/ClientContact/saveWeights?refreshDupes=${redupe}&resync=${resync}`, weightConfig);
  }

  getCompanyWeights(): Observable<any> {
    return this.http.get<ICompanyWeights>(`${environment.duplikitUrl}/company/getWeights`);
  }

  saveCompanyWeights(weightConfig: any, redupe: boolean, resync = false): Observable<any> {
    return this.http.post<ICompanyWeights>(
      `${environment.duplikitUrl}/ClientCorporation/saveWeights?refreshDupes=${redupe}&resync=${resync}`, weightConfig);
  }

  getAll(): Observable<IEntityWeights> {
    return this.http.get<IEntityWeights>(`${environment.duplikitUrl}/weights/getAllCurrentWeights`);
  }

  getAvailableWeightsForSelection(): Observable<IBullhornFields> {
    return this.http.get<any>(`${environment.duplikitUrl}/weights/getAllAvailableExtraWeights`);
  }

  saveAll(): Observable<(ICandidateWeights | IContactWeights | ICompanyWeights)[]> {
    const observables: Observable<ICandidateWeights | IContactWeights | ICompanyWeights>[] = [];
    for (const entity of ['candidate', 'contact', 'company'] as const) {
      if (this.hasChanged[entity]) {
        const resync = this.needsResync[entity];
        switch (entity) {
          case 'candidate':
            observables.push(this.saveCandidateWeights(this.weights.candidate, true, resync));
            break;
          case 'contact':
            observables.push(this.saveContactWeights(this.weights.contact, true, resync));
            break;
          case 'company':
            observables.push(this.saveCompanyWeights(this.weights.company, true, resync));
            break;
        }
      }
    }
    return forkJoin(observables);
  }

  getAllCustomSelections(): Observable<ICustomPairings> {
    return this.http.get<ICustomPairings>(`${environment.duplikitUrl}/weights/getAllCustomSelections`);
  }

  saveCustomSelections(): Observable<any> {
    return this.http.post<ICustomPairings>(
      `${environment.duplikitUrl}/weights/saveAllCustomSelections`,
      JSON.stringify(this.customWeightSelection));
  }
}
